import { useEffect, useState } from 'react';

import { logger } from '@azure/storage-file-datalake';
import { Stack } from '@mui/material';
import * as Ably from 'ably';
import { useChannel } from 'ably/react';

import { IAccount } from '@/models';
import ActiveViewers from '@/pages/CFS/Edit/components/ActiveViewers';
import { useUserStore } from '@/store'; // Assuming you have this for currentUser

interface Viewer {
  email: string;
  name: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  timestamp?: string;
}

export const CFSActiveViewers = ({
  cfsId,
  account,
}: {
  cfsId: string | undefined;
  account: IAccount | null;
}) => {
  const [activeViewers, setActiveViewers] = useState<Viewer[]>([]);
  const { user: currentUser } = useUserStore();

  const { channel } = useChannel(
    `account:${account?._id}:cfs:${cfsId}-presence`,
  );

  useEffect(() => {
    if (!channel || !cfsId || !currentUser) return;

    const filterViewers = (viewers: Viewer[]) => {
      return viewers.filter((viewer) => viewer.email !== currentUser.email);
    };

    channel.presence.enter({
      email: currentUser.email,
      name: currentUser.fullName,
      firstName: currentUser.firstName,
      middleName: currentUser.middleName,
      lastName: currentUser.lastName,
      timestamp: new Date().toISOString(),
    });

    channel.presence
      .get()
      .then((members: Ably.Types.PresenceMessage[]) => {
        const currentViewers = members.map((member) => member.data);
        setActiveViewers(filterViewers(currentViewers));
      })
      .catch((err) => {
        logger.error('Error getting presence members:', err);
      });

    const handleEnter = (member: Ably.Types.PresenceMessage) => {
      if (member.data.email === currentUser.email) return; // Ignore self

      setActiveViewers((prev) => {
        const exists = prev.some((v) => v.email === member.data.email);
        if (!exists) {
          return [...prev, member.data];
        }
        return prev;
      });
    };

    const handleLeave = (member: Ably.Types.PresenceMessage) => {
      setActiveViewers((prev) =>
        prev.filter((viewer) => viewer.email !== member.data.email),
      );
    };

    channel.presence.subscribe('enter', handleEnter);
    channel.presence.subscribe('leave', handleLeave);

    return () => {
      channel.presence.leave();
      channel.presence.unsubscribe();
    };
  }, [channel, cfsId, currentUser, account?._id]);

  return (
    <Stack direction="row" alignItems="center" columnGap={1}>
      <ActiveViewers viewers={activeViewers} />
    </Stack>
  );
};
